import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import {
  StockMovement,
  StockMovementPayload,
  StockMovementType,
  StockAuditRow,
  STOCK_MOVEMENT_SIGN
} from '../types/StockMovement.types';
import { Book } from '../types/Book.types';
import { getAccessToken, buildQuery, unwrapList, unwrapRecord, unwrapError } from './apiUtils';

const ROUTES = {
  list:   '/stock_movements_list',
  create: '/stock_movements_create'
};

const MOVEMENT_TYPES: StockMovementType[] = ['purchase', 'sale', 'inventory_adjustment', 'opening_balance'];

export const mapStockMovement = (raw: any): StockMovement => ({
  id:             String(raw.id),
  warehouseId:    raw.warehouse_id !== undefined && raw.warehouse_id !== null ? String(raw.warehouse_id) : '',
  warehouseName:  raw.warehouse_name || '',
  bookId:         raw.book_id !== undefined && raw.book_id !== null ? String(raw.book_id) : '',
  bookTitle:      raw.book_title || '',
  bookIsbn:       raw.book_isbn || '',
  movementType:   MOVEMENT_TYPES.indexOf(raw.movement_type) >= 0 ? raw.movement_type : 'inventory_adjustment',
  quantity:       isNaN(Number(raw.quantity)) ? 0 : Number(raw.quantity),
  movementDate:   raw.movement_date || '',
  sourceTable:    raw.source_table || '',
  sourceRecordId: raw.source_record_id !== undefined && raw.source_record_id !== null ? String(raw.source_record_id) : ''
});

export const listStockMovements = async (
  options: { warehouseId?: string; bookId?: string; movementType?: string; page?: number; pageSize?: number } = {}
): Promise<StockMovement[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:  token,
    warehouse_id:  options.warehouseId,
    book_id:       options.bookId,
    movement_type: options.movementType,
    page:          options.page || 1,
    page_size:     options.pageSize || 500
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
  return unwrapList(res).map(mapStockMovement);
};

export const createStockMovement = async (payload: StockMovementPayload): Promise<StockMovement> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:     token,
    warehouse_id:     payload.warehouseId,
    book_id:          payload.bookId,
    movement_type:    payload.movementType,
    quantity:         payload.quantity,
    movement_date:    payload.movementDate,
    source_table:     payload.sourceTable || 'manual',
    source_record_id: payload.sourceRecordId
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al registrar el movimiento de stock.'));
  return mapStockMovement(record);
};

/**
 * Deriva el informe de auditoría a partir de los movimientos. Todo el stock
 * sale de stock_movements, así que no hay más lógica que agregar por
 * (almacén, libro) y sumar respetando el signo de cada tipo de movimiento.
 *
 * `books` aporta el precio y el stock declarado en la ficha. El único stock
 * real es `currentStock`, derivado de los movimientos, y sobre él se calculan
 * valor, stock medio y rotación; `declaredStock` se arrastra tal cual, solo
 * informativo, porque hoy `books` no guarda existencias (ver mapBook en
 * BooksService) y llega siempre a 0. Sin `books` la fila sale completa salvo
 * `stockValue`, que queda a 0 €.
 */
export const buildStockAudit = (movements: StockMovement[], books: Book[] = []): StockAuditRow[] => {
  const rows: Record<string, StockAuditRow> = {};
  const bookById = new Map(books.map(b => [b.id, b]));

  movements.forEach((mov) => {
    const key = `${mov.warehouseId}::${mov.bookId}`;
    if (!rows[key]) {
      rows[key] = {
        warehouseId:      mov.warehouseId,
        warehouseName:    mov.warehouseName,
        bookId:           mov.bookId,
        bookTitle:        mov.bookTitle,
        bookIsbn:         mov.bookIsbn,
        purchases:        0,
        sales:            0,
        adjustments:      0,
        openingBalance:   0,
        currentStock:     0,
        movementCount:    0,
        lastMovementDate: '',
        declaredStock:    0,
        stockValue:       0,
        averageStock:     0,
        rotationIndex:    0
      };
    }

    const row = rows[key];
    const qty = Math.abs(mov.quantity);

    if (mov.movementType === 'purchase') row.purchases += qty;
    else if (mov.movementType === 'sale') row.sales += qty;
    else if (mov.movementType === 'inventory_adjustment') row.adjustments += mov.quantity;
    else row.openingBalance += mov.quantity;

    // La cantidad ya viene firmada desde la base de datos; el signo por tipo
    // solo se aplica cuando el registro llega sin signo (altas manuales).
    const signed = mov.quantity < 0 ? mov.quantity : qty * STOCK_MOVEMENT_SIGN[mov.movementType];
    row.currentStock += signed;
    row.movementCount += 1;
    if (mov.movementDate > row.lastMovementDate) row.lastMovementDate = mov.movementDate;
  });

  return Object.keys(rows).map((k) => {
    const row = rows[k];
    const book = bookById.get(row.bookId);
    // Lo que declara la ficha, sin más. Sin fallback a currentStock: eso solo
    // haría que la columna se copiase a sí misma y pareciese un dato donde no
    // lo hay.
    row.declaredStock = book?.stock || 0;
    // Los cálculos van sobre el stock derivado de los movimientos, que es el
    // único que existe de verdad.
    row.stockValue = row.currentStock * (book?.price || 0);
    row.averageStock = (row.openingBalance + row.currentStock) / 2;
    row.rotationIndex = row.averageStock === 0 ? 0 : row.sales / row.averageStock;
    return row;
  });
};

export interface WarehouseStockRow {
  bookId: string;
  title: string;
  isbn: string;
  stock: number;
}

/**
 * Existencias reales de un almacén (stock_levels_list con umbral alto): suma de
 * sus movimientos por libro, con la misma regla de signo que books_list. El
 * endpoint pagina a 200 como máximo, así que se pide por páginas hasta
 * completar total_count. Los libros sin movimientos salen con stock 0.
 */
export const listWarehouseStock = async (warehouseId: string): Promise<WarehouseStockRow[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const rows: WarehouseStockRow[] = [];
  const pageSize = 200;
  for (let page = 1; page <= 50; page++) {
    const res = await ObexRequestsHandlerP80.get(`/stock_levels_list${buildQuery({
      access_token: token,
      warehouse_id: warehouseId,
      max_stock:    1000000000,
      page,
      page_size:    pageSize
    })}`);
    const raw = unwrapList(res);
    raw.forEach(r => rows.push({
      bookId: String(r.id),
      title:  r.title || '',
      isbn:   r.isbn || '',
      stock:  isNaN(Number(r.stock)) ? 0 : Number(r.stock)
    }));
    const total = raw.length > 0 && !isNaN(Number(raw[0].total_count)) ? Number(raw[0].total_count) : rows.length;
    if (raw.length < pageSize || rows.length >= total) break;
  }
  return rows;
};

/**
 * Todos los movimientos, no solo la primera página: stock_movements_list ordena
 * por id descendente y pagina, y el informe suma desde el primer movimiento, así
 * que con una sola página el stock actual saldría mal en cuanto hubiera más.
 */
export const listAllStockMovements = async (options: { warehouseId?: string } = {}): Promise<StockMovement[]> => {
  const pageSize = 500;
  const all: StockMovement[] = [];
  for (let page = 1; page <= 200; page++) {
    const chunk = await listStockMovements({ warehouseId: options.warehouseId, page, pageSize });
    all.push(...chunk);
    if (chunk.length < pageSize) break;
  }
  return all;
};
